import React from 'react';
import { Sparkles, Save, DollarSign, Loader } from 'lucide-react';
import type { FileUpload as FileUploadType, CostEstimation } from '../types';
import { theme } from '../styles/theme';
import ModelSelect from './ModelSelect';

interface ProcessingConfigTabProps {
  selectedFile: FileUploadType | null;
  prompt: string;
  modelName: string;
  setModelName: (name: string) => void;
  temperature: number;
  setTemperature: (temp: number) => void;
  chunkSize: number;
  setChunkSize: (size: number) => void;
  maxConcurrent: number;
  setMaxConcurrent: (max: number) => void;
  outputMultiplier: number;
  setOutputMultiplier: (multiplier: number) => void;
  estimation: CostEstimation | null;
  estimating: boolean;
  estimatedConfig: Record<string, unknown> | null;
  hasConfigChanged: () => boolean;
  handleEstimateForJob: () => void;
  handleStartJob: () => void;
  showSaveTemplate: boolean;
  setShowSaveTemplate: (show: boolean) => void;
  promptName: string;
  setPromptName: (name: string) => void;
  promptDescription: string;
  setPromptDescription: (desc: string) => void;
  handleSaveTemplate: () => void;
}

const ProcessingConfigTab: React.FC<ProcessingConfigTabProps> = ({
  selectedFile,
  prompt,
  modelName,
  setModelName,
  temperature,
  setTemperature,
  chunkSize,
  setChunkSize,
  maxConcurrent,
  setMaxConcurrent,
  outputMultiplier,
  setOutputMultiplier,
  estimation,
  estimating,
  estimatedConfig,
  hasConfigChanged,
  handleEstimateForJob,
  handleStartJob,
  showSaveTemplate,
  setShowSaveTemplate,
  promptName,
  setPromptName,
  promptDescription,
  setPromptDescription,
  handleSaveTemplate,
}) => {
  return (
    <div className={`${theme.card.background} rounded-lg shadow-lg p-6`}>
      <h3 className={`text-lg font-semibold ${theme.text.primary} mb-4 flex items-center`}>
        <Sparkles className="w-5 h-5 mr-2 text-text-secondary" />
        Processing Configuration
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Model Selection */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            AI Model
          </label>
          {/* Decision: PDR-002 follow-up — prefer the cheap 'gemini-2.5-flash'
              backend default over ModelSelect's alphabetical-first fallback
              (which sorts to a claude-* model, the most expensive option). */}
          <ModelSelect value={modelName} onChange={setModelName} preferredDefault="gemini-2.5-flash" />
        </div>

        {/* Temperature */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Temperature: {temperature.toFixed(1)}
          </label>
          <input
            type="range"
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            min="0.0"
            max="1.0"
            step="0.1"
            className="w-full"
          />
          <div className="flex justify-between text-xs text-text-tertiary mt-1">
            <span>0.0 (Precise)</span>
            <span>0.5 (Balanced)</span>
            <span>1.0 (Creative)</span>
          </div>
        </div>

        {/* Chunk Size */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Chunk Size (Rows per Batch)
          </label>
          <input
            type="number"
            value={chunkSize}
            onChange={(e) => setChunkSize(Math.max(1, Math.min(100, parseInt(e.target.value) || 20)))}
            min="1"
            max="100"
            className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
          />
          <p className="text-xs text-text-tertiary mt-1">
            Smaller = more API calls, larger = fewer calls (1-100)
          </p>
        </div>

        {/* Max Concurrent */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Max Concurrent Requests
          </label>
          <input
            type="number"
            value={maxConcurrent}
            onChange={(e) => setMaxConcurrent(Math.max(1, Math.min(50, parseInt(e.target.value) || 5)))}
            min="1"
            max="50"
            className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
          />
          <p className="text-xs text-text-tertiary mt-1">
            Higher = faster but may hit rate limits (1-50)
          </p>
        </div>
        
        {/* Output Multiplier */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Output Token Multiplier
          </label>
          <input
            type="number"
            value={outputMultiplier}
            onChange={(e) => setOutputMultiplier(Math.max(0.1, parseFloat(e.target.value) || 1.8))}
            min="0.1"
            step="0.1"
            className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
          />
          <p className="text-xs text-text-tertiary mt-1">
            Estimate output tokens as input × this value (default: 1.8)
          </p>
        </div>
      </div>

      {/* Change Detection Warning Banner */}
      {estimation && estimatedConfig && hasConfigChanged() && (
        <div className="bg-bg-subtle rounded-lg p-4 border-2 border-border mb-4 animate-pulse">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-text-secondary">
                Configuration Changed - Estimation Outdated
              </h3>
              <div className="mt-2 text-sm text-text-secondary">
                <p>Your current settings have changed since the last cost estimation:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {estimatedConfig.fileId !== selectedFile?.id && (
                    <li><strong>File changed:</strong> Different file selected</li>
                  )}
                  {estimatedConfig.prompt !== prompt && (
                    <li><strong>Prompt changed:</strong> Prompt text has been modified</li>
                  )}
                  {estimatedConfig.modelName !== modelName && (
                    <li><strong>Model changed:</strong> {String(estimatedConfig.modelName)} → {modelName}</li>
                  )}
                  {estimatedConfig.outputMultiplier !== outputMultiplier && (
                    <li><strong>Output multiplier changed:</strong> {String(estimatedConfig.outputMultiplier)}x → {outputMultiplier}x</li>
                  )}
                </ul>
              </div>
              <div className="mt-3">
                <button
                  onClick={handleEstimateForJob}
                  disabled={estimating}
                  className="inline-flex items-center px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {estimating ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Estimating...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Re-estimate Cost
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Estimation Display */}
      {estimation && estimation.estimated_tokens && estimation.estimated_cost && (
        <div className="bg-bg-subtle rounded-lg p-6 border-2 border-interactive-primary/30 mb-4">
          <div className="flex items-center mb-4">
            <DollarSign className="w-6 h-6 text-interactive-primary mr-2" />
            <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
              Cost Estimation for {estimation.model_name || 'Selected Model'}
              {!hasConfigChanged() && (
                <span className="ml-2 text-sm font-normal text-qc-success"> Current</span>
              )}
            </h3>
            {estimating && (
              <Loader className="w-4 h-4 ml-2 animate-spin text-interactive-primary" />
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`${theme.card.background} rounded-lg p-4 shadow-sm`}>
              <p className={`text-sm ${theme.text.secondary} mb-1`}>Rows to Process</p>
              <p className={`text-2xl font-bold ${theme.text.primary}`}>
                {(estimation.row_count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                from {estimation.filename}
              </p>
            </div>
            
            <div className={`${theme.card.background} rounded-lg p-4 shadow-sm`}>
              <p className={`text-sm ${theme.text.secondary} mb-1`}>Total Tokens (All Rows)</p>
              <p className={`text-2xl font-bold ${theme.text.primary}`}>
                {(estimation.estimated_tokens.grand_total_tokens || 0).toLocaleString()}
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                {(estimation.estimated_tokens.total_input_tokens || 0).toLocaleString()} input + {(estimation.estimated_tokens.total_output_tokens || 0).toLocaleString()} output
              </p>
            </div>
            
            <div className={`${theme.card.background} rounded-lg p-4 shadow-sm`}>
              <p className={`text-sm ${theme.text.secondary} mb-1`}>Total Cost (USD)</p>
              <p className="text-2xl font-bold text-interactive-primary">
                ${(estimation.estimated_cost.usd || 0).toFixed(4)}
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                ${((estimation.estimated_cost.per_row_usd || 0)).toFixed(6)} per row
              </p>
            </div>
            
            <div className={`${theme.card.background} rounded-lg p-4 shadow-sm`}>
              <p className={`text-sm ${theme.text.secondary} mb-1`}>Total Cost (SGD)</p>
              <p className="text-2xl font-bold text-qc-success">
                ${(estimation.estimated_cost.sgd || 0).toFixed(4)}
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                @ 1.35 USD/SGD
              </p>
            </div>
          </div>

          {/* Chunk Information */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-bg-subtle border border-border rounded-lg p-3">
              <p className="text-xs text-text-secondary font-medium mb-1">Processing Chunks</p>
              <p className="text-lg font-bold text-text-secondary">
                {(estimation.total_chunks || 0).toLocaleString()} chunks
              </p>
              <p className="text-xs text-text-secondary mt-0.5">
                {estimation.chunk_size} rows per chunk = {estimation.total_chunks} API requests
              </p>
            </div>
            
            <div className="bg-interactive-primary/10 border border-interactive-primary/30 rounded-lg p-3">
              <p className="text-xs text-interactive-primary font-medium mb-1">Tokens per Chunk</p>
              <p className="text-lg font-bold text-interactive-primary">
                {(estimation.estimated_tokens.total_tokens_per_chunk || 0).toLocaleString()} tokens
              </p>
              <p className="text-xs text-interactive-primary mt-0.5">
                {(estimation.estimated_tokens.input_tokens_per_chunk || 0).toLocaleString()} input + {(estimation.estimated_tokens.estimated_output_tokens_per_chunk || 0).toLocaleString()} output
              </p>
            </div>
            
            <div className="bg-bg-subtle border border-border rounded-lg p-3">
              <p className="text-xs text-text-secondary font-medium mb-1">Cost per Chunk</p>
              <p className="text-lg font-bold text-text-secondary">
                ${(estimation.estimated_cost.per_chunk_usd || 0).toFixed(6)}
              </p>
              <p className="text-xs text-text-secondary mt-0.5">
                USD (${(estimation.estimated_cost.per_chunk_sgd || 0).toFixed(6)} SGD)
              </p>
            </div>
          </div>

          {estimation.pricing_note && (
            <div className={`mt-4 p-3 ${theme.status.info.background} border border-interactive-primary/30 rounded-lg`}>
              <p className="text-sm text-interactive-primary">
                {estimation.pricing_note}
              </p>
            </div>
          )}

          <div className="mt-4 p-3 bg-qc-warning/10 border border-qc-warning/30 rounded-lg">
            <p className="text-sm text-qc-warning">
              <strong>Note:</strong> This is an estimate based on counted input tokens and estimated output. Actual costs may vary.
            </p>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplate && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-6 max-w-md w-full mx-4`}>
            <h3 className={`text-xl font-semibold ${theme.text.primary} mb-4`}>
              Save Prompt Template
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Template Name
                </label>
                <input
                  type="text"
                  value={promptName}
                  onChange={(e) => setPromptName(e.target.value)}
                  placeholder="e.g., Product Analysis"
                  className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Description (Optional)
                </label>
                <textarea
                  value={promptDescription}
                  onChange={(e) => setPromptDescription(e.target.value)}
                  placeholder="What does this template do?"
                  rows={3}
                  className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowSaveTemplate(false)}
                className={`px-4 py-2 bg-bg-hover ${theme.text.secondary} rounded-lg hover:bg-bg-hover`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!promptName}
                className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary text-text-inverse rounded-lg"
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 mt-6">
        <button
          onClick={() => setShowSaveTemplate(true)}
          disabled={!prompt}
          className="px-6 py-3 bg-bg-subtle hover:bg-bg-hover border border-border-strong disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed text-text-primary font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <Save className="w-5 h-5" />
          <span>Save as Template</span>
        </button>
        <button
          onClick={handleEstimateForJob}
          disabled={!selectedFile || !prompt || estimating}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          {estimating ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Estimating...</span>
            </>
          ) : (
            <>
              <DollarSign className="w-5 h-5" />
              <span>Estimate Cost</span>
            </>
          )}
        </button>
        <button
          onClick={handleStartJob}
          disabled={!selectedFile || !prompt || !estimation}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <Sparkles className="w-5 h-5" />
          <span>Start Processing Job</span>
        </button>
      </div>
    </div>
  );
};

export default ProcessingConfigTab;
